import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { onboardingService } from "services/onboarding-service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { CircularProgress } from "@mui/material";
import OnboardingLayout from "./OnboardingLayout";

/**
 * Simple continuation page displayed when a customer follows the short URL.
 * It fetches the claim status and allows the customer to acknowledge completion.
 */
const CustomerContinuation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [claim, setClaim] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await onboardingService.renderContinuationPage(token);
        setClaim(res.data || res);
      } catch (err) {
        console.error(err);
        setClaim({ error: err.message || "Failed to load claim" });
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [token]);

  const handleComplete = async () => {
    setActionLoading(true);
    try {
      await onboardingService.completeCustomerContinuation(token);
      // After completion, redirect to a success page or home
      navigate("/onboard/success");
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Error completing continuation");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <OnboardingLayout>
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </OnboardingLayout>
    );
  }

  if (claim?.error) {
    return (
      <OnboardingLayout>
        <MDBox p={4} textAlign="center">
          <MDTypography variant="h5" color="error" gutterBottom>
            {claim.error}
          </MDTypography>
        </MDBox>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout>
      <MDBox p={4} textAlign="center">
        <MDTypography variant="h4" gutterBottom>
          Welcome back!
        </MDTypography>
        <MDTypography variant="body1" gutterBottom>
          Claim ID: {claim.claim_id}
        </MDTypography>
        <MDTypography variant="body2" gutterBottom>
          Current Status: {claim.status}
        </MDTypography>
        <MDButton
          variant="gradient"
          color="info"
          onClick={handleComplete}
          disabled={actionLoading}
          sx={{ mt: 3 }}
        >
          {actionLoading ? "Completing..." : "Complete Registration"}
        </MDButton>
      </MDBox>
    </OnboardingLayout>
  );
};

export default CustomerContinuation;
